// EVE-Profile-Store. V1-Pendant: localStorage key `eve_profile_v1_<userId>`.
// V3-Refactor: Per-userId Storage über manuelle load/save (zustand persist
// kann keine dynamischen Keys). Backend-Sync ist optional (fire-and-forget).

import { create } from 'zustand';
import { DEFAULT_PROFILE, mergeWithDefaults } from '@/lib/eve-profile';
import type { EveProfile } from '@/types/eve-profile';

const BASE_KEY = 'eve_profile_v3';

export function eveProfileKey(userId: string | null | undefined): string {
  return userId ? `${BASE_KEY}_${userId}` : BASE_KEY;
}

function readFromStorage(userId: string | null): EveProfile {
  try {
    const raw = localStorage.getItem(eveProfileKey(userId));
    if (!raw) return { ...DEFAULT_PROFILE };
    return mergeWithDefaults(JSON.parse(raw) as Partial<EveProfile>);
  } catch {
    return { ...DEFAULT_PROFILE };
  }
}

function writeToStorage(userId: string | null, profile: EveProfile): void {
  try {
    localStorage.setItem(eveProfileKey(userId), JSON.stringify(profile));
  } catch (e) {
    console.warn('[EveProfile] Save fehlgeschlagen:', e instanceof Error ? e.message : e);
  }
}

export interface EveProfileState {
  userId: string | null;
  profile: EveProfile;
}

export interface EveProfileActions {
  /** Lädt das Profil für einen User aus localStorage. */
  loadForUser: (userId: string | null) => void;
  /** Patch + sofort persistieren. */
  update: (patch: Partial<EveProfile>) => EveProfile;
  /** Ersetzt das gesamte Profil (z.B. nach Backend-Sync). */
  replace: (profile: EveProfile) => void;
  /** Resettet auf Defaults für den aktuellen User. */
  reset: () => void;
}

export type EveProfileStore = EveProfileState & EveProfileActions;

export const useEveProfileStore = create<EveProfileStore>()((set, get) => ({
  userId: null,
  profile: { ...DEFAULT_PROFILE },

  loadForUser: (userId) => {
    const profile = readFromStorage(userId);
    set({ userId, profile });
  },

  update: (patch) => {
    const current = get();
    const next = { ...current.profile, ...patch };
    writeToStorage(current.userId, next);
    set({ profile: next });
    return next;
  },

  replace: (profile) => {
    const merged = mergeWithDefaults(profile);
    writeToStorage(get().userId, merged);
    set({ profile: merged });
  },

  reset: () => {
    writeToStorage(get().userId, { ...DEFAULT_PROFILE });
    set({ profile: { ...DEFAULT_PROFILE } });
  },
}));
